import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";
import ClientMarquee from "@/components/shared/ClientMarquee";

const TESTIMONIALS = [
  { quote: "Horizn turned an EV launch into a city-wide event. Highway dominance, airport visibility, and measurable lift, all in 14 days.", author: "Marketing Manager" },
  { quote: "The most data-driven OOH partner we've worked with. They sweat every panel, every daypart, every metric.", author: "Brand Manager" },
  { quote: "We needed presence, not just placement. Horizn delivered a launch that everyone in the city talked about.", author: "Marketing Manager" },
  { quote: "Their creative thinking is what sets them apart. Six-word ideas that work at 80 mph.", author: "Brand Manager" },
];

const BRAND_GRID = [
  "SOLARA", "VEGA", "MAX HEALTHCARE", "MERIDIAN",
  "VOLTCRAFT", "ATLAS", "NORTHSTAR", "LRC",
  "PEEKABOO KIDZ", "SENCO", "TANISHQ", "KALYAN",
  "ELECTRO WORLD", "SQUARE YARDS", "HONEY GOLF GREENS",
  "INDANE (IOC)",
];

export default function Clients() {
  return (
    <div data-testid="clients-page" className="text-zinc-100">
      <section className="relative pt-36 md:pt-44 pb-20 border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/20 via-zinc-950 to-zinc-950" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="font-mono-accent text-[11px] text-amber-400 mb-5">Clients</div>
          <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter max-w-4xl">
            Built with <span className="gradient-text italic">brave brands.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Founders, CMOs, and agencies who refuse to be ignored.
          </p>
        </div>
      </section>

      <section className="bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <ClientMarquee />
        </div>
      </section>

      <section className="bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28">
          <SectionHeading overline="The roster" title="A brand wall built on trust." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {BRAND_GRID.map((b, i) => (
              <motion.div
                key={b}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.02 }}
                className="aspect-[3/2] rounded-xl border border-white/5 bg-zinc-900/40 flex items-center justify-center text-center px-3 hover:border-blue-500/30 hover:bg-zinc-900/70 transition-colors duration-500 group"
                data-testid={`brand-tile-${b.toLowerCase().replace(/ /g, "-")}`}
              >
                <span className="font-display font-black text-sm md:text-base lg:text-lg tracking-[0.16em] text-zinc-500 group-hover:text-white transition-colors">
                  {b}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
          <SectionHeading overline="Testimonials" title="What partners say." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl border border-white/5 bg-zinc-900/50 p-8 md:p-10"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-7 w-7 text-amber-400/70" />
                <p className="mt-5 text-lg md:text-xl font-display font-medium leading-snug text-zinc-100">
                  "{t.quote}"
                </p>
                <div className="mt-6 flex items-center gap-3 text-sm">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center font-display font-bold">
                    {t.author[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-white">{t.author}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
